import { AppIconType } from '../../lib/typegen/app-icon-type';
import { ListOptionItem } from '../list-options';
import CurrencyValue from '../currency-value';
import ListOptions from '../list-options';
import ButtonRounded from '../button-rounded';
import { icons } from '../../lib/helpers/icons';
import style from './style.module.scss';
import { ICartItem } from '../../lib/types/CartItem';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useStore } from '../../lib/hooks/useStore';
import { AccountCardServerProps } from '../../lib/typegen/account-card-server-props';
import { PPConfigType } from '../../lib/typegen/pp-config-type';
import logger from '../../lib/helpers/logger';

interface AccountCardProps extends AccountCardServerProps, React.ButtonHTMLAttributes<HTMLDivElement> {
	meta?: string;
	cartItem: ICartItem;
	className?: string;
	showMeta?: boolean;
	showPastDue?: boolean;
	isMinified?: boolean;
	showPayButtonBalances?: boolean;
	showButton?: 'edit' | 'delete' | 'none';
	isHighlighted: boolean;
	onCardClick?: (id: string) => void;
	onEditClick?: (id: string) => void;
	onPayFullClick?: (id: string) => void;
	onPayPastClick?: (id: string) => void;
	onPayOtherClick?: (id: string) => void;
	onDeleteClick?: (id: string) => void;
}

const AccountCard = observer(
	({
		meta,
		metaLabel,
		accountNumberLabel,
		accountNameLabel,
		accountAddressLabel,
		pastDueLabel,
		payFullButtonProps,
		payPastButtonProps,
		payOtherButtonProps,
		cartItem,
		className,
		isMinified = false,
		showMeta = false,
		showPastDue = false,
		showPayButtonBalances = false,
		showButton = 'none',
		isHighlighted,
		onEditClick,
		onPayFullClick,
		onPayPastClick,
		onPayOtherClick,
		onCardClick,
		onDeleteClick,
	}: AccountCardProps) => {
		const Icon = icons[AppIconType.AcceptSolid];
		const [payBalanceOptions, setPayBalanceOptions] = useState<ListOptionItem[]>([]);
		const {
			configStore: { getString, companyPolicy, ppType },
		} = useStore();
		const [cardStyle, setCardStyle] = useState<string[]>([style.account, className ?? '']);
		const amountLabelKey = cartItem.isAmountConfirmed
			? 'account-card-payment-amount'
			: (ppType === PPConfigType.PayGo || ppType === PPConfigType.MyUsage) && cartItem.item.isPrePay
				? 'amount-remaining'
				: 'account-balance';
		const showAccountingFormat =
			(ppType === PPConfigType.PayGo || ppType === PPConfigType.MyUsage) && !cartItem.item.isPrePay && cartItem.amountToPay < 0;

		useEffect(() => {
			const handleSelectedEffect = () => {
				if (isHighlighted) setCardStyle([style.account, className ?? '', style['account--selected']]);
				else setCardStyle([style.account, className ?? '']);
			};

			handleSelectedEffect();
		}, [cartItem.id, className, isHighlighted]);

		if (cartItem && cartItem.taxYear) {
			meta = cartItem.taxYear;
			showMeta = true;
		}

		if (cartItem && cartItem.isInactiveAccount) {
			meta = getString('inactive-account');
			showMeta = true;
		}

		useEffect(() => {
			if (!showPayButtonBalances) {
				setPayBalanceOptions([]);
				return;
			}

			const hidePayFullOption = (ppType === PPConfigType.PayGo || ppType === PPConfigType.MyUsage) && !!cartItem.item.isPrePay;

			const options = [
				...(!hidePayFullOption
					? [
							{
								item: payFullButtonProps,
								value: cartItem.item.amountDue,
								isEnabled: !!cartItem.item.amountDue && cartItem.item.amountDue > 0,
								callback: onPayFullClick,
							},
						]
					: []),
				{
					item: payPastButtonProps,
					value: cartItem.item.amountPastDue,
					isEnabled: !!cartItem.item.amountPastDue,
					callback: onPayPastClick,
				},
				{
					item: payOtherButtonProps,
					modifier: '',
					value: undefined,
					isEnabled: true,
					callback: onPayOtherClick,
				},
			]
				.map(({ item, modifier, value, isEnabled, callback }) => {
					return (
						item && {
							id: cartItem.id,
							icon: item.icon,
							label: item.label,
							linkTarget: item.target,
							isEnabled: isEnabled ?? item.isEnabled,
							className: modifier,
							value: value ?? undefined,
							callback: callback,
						}
					);
				})
				.filter((i) => i?.isEnabled);

			setPayBalanceOptions(options.length ? options : []);
		}, [
			cartItem,
			onPayFullClick,
			onPayPastClick,
			onPayOtherClick,
			showPayButtonBalances,
			ppType,
			payFullButtonProps,
			payPastButtonProps,
			payOtherButtonProps,
		]);

		if (!cartItem) return <></>;

		return (
			<div
				className={cardStyle.join(' ')}
				onClick={() => {
					onCardClick?.(cartItem.id);
				}}
			>
				<div className={style.account__info}>
					{showMeta && (metaLabel || meta) && (
						<div className={style.info__meta}>
							{metaLabel ? getString(metaLabel) : ''} {meta}
						</div>
					)}
					<ul className={[style['info__kvp-list'], style['kvp-list']].join(' ')}>
						<li className={[style['kvp-list__item'], style.item].join(' ')}>
							<span className={style.item__label}>{getString(accountNumberLabel)}:</span>
							<span className={style.item__value}>
								{companyPolicy.obscuring.screenItemNumber
									? (cartItem.item.obscuredItemNumber ?? cartItem.item.itemNumber)
									: cartItem.item.itemNumber}
							</span>
						</li>
						{(cartItem.item.customerName || cartItem.customer) && (
							<li className={[style['kvp-list__item'], style.item].join(' ')}>
								<span className={style.item__label}>{getString(accountNameLabel)}:</span>
								<span className={style.item__value}>{cartItem.item.customerName ?? cartItem.customer?.name}</span>
							</li>
						)}
						{cartItem.item.address && (
							<li className={[style['kvp-list__item'], style.item].join(' ')}>
								<span className={style.item__label}>{getString(accountAddressLabel ?? 'address')}:</span>
								<span className={style.item__value}>{cartItem.item.address}</span>
							</li>
						)}
						{!isMinified && (
							<li className={[style['kvp-list__item'], style.item, cartItem.isAmountConfirmed ? style['item--checked'] : ''].join(' ')}>
								<span className={style.item__label}>{getString(amountLabelKey)}:</span>
								<CurrencyValue value={cartItem.amountToPay} className={style.item__value} useAccountingFormat={showAccountingFormat} />
								{cartItem.isAmountConfirmed && <Icon className={style.item__icon} />}
							</li>
						)}
						{showPastDue && !isMinified && !!cartItem.item.amountPastDue && (
							<li className={[style['kvp-list__item'], style.item].join(' ')}>
								<span className={style.item__label}>{getString(pastDueLabel)}:</span>
								<CurrencyValue value={cartItem.item.amountPastDue} className={style.item__value} />
							</li>
						)}
					</ul>
					{showPayButtonBalances && (
						<ListOptions className={style.info__amounts} options={payBalanceOptions} useSeparator={true} useCurrencySymbol={true} />
					)}
				</div>
				{showButton == 'edit' && (
					<ButtonRounded
						onClick={() => {
							logger.logGeneral.log({}, 'Edit button clicked for cart item:', cartItem.id);
							onEditClick?.(cartItem.id);
						}}
						iconType={AppIconType.Pencil}
						className={style.account__action}
					/>
				)}
				{showButton == 'delete' && (
					<ButtonRounded
						onClick={() => {
							logger.logGeneral.log({}, 'Delete button clicked for cart item:', cartItem.id);
							onDeleteClick?.(cartItem.id);
						}}
						iconType={AppIconType.Delete}
						className={style.account__action}
					/>
				)}
			</div>
		);
	}
);

export default AccountCard;
